import { ComponentBase } from "./components/ComponentBase.js";
import { template } from './utils/TemplateParser';
import { Dropdown } from './utils/Dropdown';
import { vars } from './utils/vars';
import { api } from './utils/api';
import { lang, setLanguage } from './lang';
import { loadingScreen } from './components/LoadingScreen';

// Font Awesome
import {
    config as faConfig,
    dom as faDom,
    library as faLibrary } from '@fortawesome/fontawesome-svg-core';
import {faRss, faCalendarAlt, faMapMarkerAlt, faTicketAlt, faTimes, faClock, faEnvelopeOpenText, faUsersClass, faLaptopHouse, faLink, faChevronLeft, faChevronRight, faStar, faInfoCircle, faCalendarEdit, faDownload} from '@fortawesome/pro-solid-svg-icons';
import {faAngleDown, faListAlt, faTh, faSlidersH, faCalendarCheck} from '@fortawesome/pro-regular-svg-icons';
faConfig.autoAddCss = false;
faLibrary.add(faRss, faCalendarAlt, faMapMarkerAlt, faTicketAlt, faTimes, faClock, faEnvelopeOpenText, faUsersClass, faLaptopHouse, faLink, faChevronLeft, faChevronRight, faStar, faInfoCircle, faCalendarEdit, faDownload, // Solid
     faAngleDown, faListAlt, faTh, faSlidersH, faCalendarCheck);	// Regular 

export class Wrapper extends ComponentBase {
    constructor(options = {}) {
        super(options);
        this.options = options;
        Promise.all([
            this.fetch(),
            setLanguage('en')
        ])
        .then(() => {
            this.render();
            if (this.options.afterInit && typeof this.options.afterInit == 'function') {
                this.options.afterInit();
            }
        })
        .catch(err => {
            console.log("err: ", err);
            this.render(template`<p>There was an error. Please try again.</p>`);
        });
    }

    registerEvents() {
        this.addEventListener(this.el, 'click', (e) => {
            if (!e.target.closest('.dropdown') && !e.target.closest('.mc-calendar')) {
                Dropdown.closeAllDropdowns();
            }
        }, {capture: true});
        return {
            'click          .dropdown-trigger' : 'toggleDropdown',
            'renderDone     #view-container'   : 'convertIcons',
            'convertIcons'  : 'convertIcons',
        };
    }

    template() {
        if(vars.pubcalendar.personalization) {
            const p13nScriptTag = document.createElement('script');
            p13nScriptTag.src = '/cms-p13n.js';
            p13nScriptTag.async = true;
            document.getElementsByTagName("head")[0].appendChild(p13nScriptTag);
        }
        return template`
            <style>
                #view-container {
                    display: none;
                }
                .mc-calendar {
                    display: none; /* initially hidden to prevent flash of unstyled content */
                }
            </style>
            <link id="main-css" rel="stylesheet" href="${this.getPublicAppBaseURL()}style.css?v=${vars.version}">
            ${this.customCSSTemplate()}
            <!-- icon styles -->
            <style>${faDom.css()}</style>
            <style>
                :host, :root {
                    --mc-theme-color-main: ${vars.pubcalendar.color};
                    --mc-font-family: ${vars.pubcalendar.font};
                }
            </style>
            <div id="view-container" role="region" aria-labelledby="hero-title hero-subtitle"></div>
        `;
    }

    getPublicAppBaseURL(){
        const scriptTag = document.getElementById('omnicms-calendar');
        return scriptTag.src.replace(/app\.js$/, '');
    }

    customCSSTemplate(){
        const css = document.querySelector('omnicms-calendar[data-css]')?.getAttribute('data-css');
        return css ? template`<link rel="stylesheet" href="${css}">` : '';
    }

    setEmbedOverrides(data) {
        const embedOverrides = {};
        [
            'title',
            'subtitle',
            'image',
            'imageAltText',
            'layout',
            'eventType',
            'featuredSlider',
            'autoscroll',
            'color',
            'googleMapKey',
            'personalization',
            'basepath',
        ].forEach(i => {
            const kebabCaseID = this.makeKebabCase(i);
            const attributeValue = document.querySelector(`omnicms-calendar[data-${kebabCaseID}]`)?.getAttribute(`data-${kebabCaseID}`);
            if(attributeValue !== undefined) embedOverrides[i] = attributeValue;
        });

        if(!['LIST','GRID'].includes(embedOverrides.layout)) delete embedOverrides.layout;
        if(!['FEATURED','ALL'].includes(embedOverrides.eventType)) delete embedOverrides.eventType;

        [
            'featuredSlider',
            'autoscroll',
            'personalization',
        ].forEach(i => {
            if(['true','false'].includes(embedOverrides[i])) {
                embedOverrides[i] = embedOverrides[i] == 'true';
            } else {
                delete embedOverrides[i];
            }
        });
        
        return {...data, ...embedOverrides};
    }

    makeKebabCase(str) {
		return str.replace(/([A-Z])/g, '-$1').toLowerCase();
	}

    fetch() {
        return api.pubcalendar()
        .then(data => {
            data.eventType = data.eventType ?? 'FEATURED';
            data.autoscroll = data.autoscroll ?? true;
            data.featuredSlider = data.featuredSlider ?? false;
            data = this.setEmbedOverrides(data);
            if (data.timezone == "") {
                data.timezone = "UTC";
            }
            vars.pubcalendar = data;    // save data
        })
        .catch(err => {
            this.render(template`<p>${lang.ErrorContactingServerMessage}</p>`);
            // TODO: handle error
            console.log('err: ', err);
        });
    }

    afterRender() {
        vars.viewContainer = this.el.querySelector('#view-container');
        // Try to attach loading screen removal to load of the stylesheet to maintain UAT goals
        const linkEl = this.el.querySelector('#main-css');
        if (linkEl) {
            // Stylesheet loaded successfully
            linkEl.addEventListener('load', () => {
                loadingScreen.remove();
            });
            // Stylesheet was blocked or failed to load, still want to remove loading screen
            linkEl.addEventListener('error', () => {
                loadingScreen.remove();
            });
        }
        // Link element will sometimes be missing if the data fetch fails, still want to remove loading screen
        else {
            loadingScreen.remove();
        }
    }

    setView(view) {
        if (vars.currentView) {
            vars.currentView.destroy();
        }
        vars.viewContainer.innerHTML = '';  // empty view container
        vars.viewContainer.appendChild(view.el);
        vars.currentView = view;
        window.scrollTo(0,0);   // Set scroll back to top of page
    }

    convertIcons() {
        faDom.i2svg({ node: vars.calendarShadowDom });
    }

    toggleDropdown(e) {
        const dropdown = e.target.closest('.dropdown');
        // Don't open a disabled dropdown
        if (!Dropdown.isDisabled(dropdown)){
            if (Dropdown.isOpen(dropdown)) {
                // Close
                Dropdown.close(dropdown);
            } else {
                // Open
                Dropdown.open(dropdown);
            }
        }
    }
}